import { reactive, readonly, inject, computed, ref, type Ref, type ComputedRef } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import type { AxiosInstance } from 'axios'
import { isEmpty } from 'lodash-es'

import type { PitcherInfo } from '@lib/types/app'
import type { User } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'

export interface SmartFolderProperty {
  name: string
  display_name?: string
  accessor: string
  type: string
  description?: string
  display_names_by_value?: Record<string, string>
}

export interface SectionById {
  id: string
  name: string
  created_at: string
  modified_at: string
  thumbnail_url?: string
  metadata?: Record<string, any>
  is_editable: boolean
  is_default: boolean
  access_type: string
  context: Record<string, any>
  used_in_count: number
  theme?: Record<string, any>
  indicators?: Record<string, any>
  tags?: string[]
}

interface PathSegment {
  value: string
  property: SmartFolderProperty
}

interface State {
  hierarchyLevels: SmartFolderProperty[]
  currentPath: PathSegment[]
  availableValues: Array<{ value: string; files_count: number }>
  matchingSections: SectionById[]
  isLoading: boolean
  isSavingConfig: boolean
  viewMode: 'folders' | 'sections'
  totalCount: number
  page: number
  pageSize: number
  includeDraft: boolean
}

const state = reactive<State>({
  hierarchyLevels: [],
  currentPath: [],
  availableValues: [],
  matchingSections: [],
  isLoading: false,
  isSavingConfig: false,
  viewMode: 'folders',
  totalCount: 0,
  page: 1,
  pageSize: 50,
  includeDraft: true,
})

export default function useSectionSmartFolderStore() {
  const pitcherInfo = inject<Ref<PitcherInfo>>('pitcherInfo')
  const myUser = inject<Ref<User>>('myUser')
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios', ref())
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )
  const smartFolderRuleAttributes = computed(() => pitcherSettings.value?.smart_folder_rule_attributes?.section || [])
  const isSmartFoldersView = useLocalStorage<boolean>(
    `isSmartFoldersView:sections:${pitcherInfo?.value?.instanceId}:${myUser?.value?.id}`,
    false,
  )

  if (!launchDarkly.value.enable_smart_folders_for_sections) {
    // If the feature flag is not enabled, we don't want to show the smart folders view
    isSmartFoldersView.value = false
  }

  async function fetchData() {
    if (!pitcherInfo?.value?.instanceId || !pitcherInfo.value.apiOrigin || !restApiAxios.value) return

    // Return early if no hierarchy levels are configured
    if (state.hierarchyLevels.length === 0) {
      state.isLoading = false
      return
    }

    try {
      state.isLoading = true
      const shouldMatch = state.currentPath.map((segment) => [segment.property.accessor, segment.value])
      const currentLevelAccessor =
        state.hierarchyLevels[Math.min(state.currentPath.length, state.hierarchyLevels.length - 1)].accessor

      const orgSubDomain = window.location.hostname.split('.')?.[0]

      // TODO: proper response type
      const data = await restApiAxios.value.post<unknown, Record<string, any>>(
        `${pitcherInfo.value.apiOrigin}/core/api/protected/canvases/sections/smart-folders`,
        {
          org_subdomain: orgSubDomain === 'localhost' ? 'dev' : orgSubDomain,
          owned_by_id: myUser?.value?.id,
          instance_id: pitcherInfo.value.instanceId,
          should_match: shouldMatch,
          folder_level: currentLevelAccessor,
          distinct: state.viewMode === 'folders',
          include_draft: state.includeDraft,
          page: state.page,
          page_size: state.pageSize,
        },
      )

      if (state.viewMode === 'folders') {
        if (!data.distinct_values?.length) {
          // No folders found, switch to sections view and fetch
          state.viewMode = 'sections'
          return fetchData() // Recursive call with new viewMode
        }
        state.availableValues = data.distinct_values.map((item: any) => ({
          files_count: item.items_count,
          file_thumbnail_urls: item.thumbnail_urls,
          type: 'smart_folder',
          value: item.value,
        }))
        state.totalCount = data.distinct_values.length
      } else {
        state.matchingSections = data.sections
        state.totalCount = data.pagination.total
      }
    } catch (error) {
      console.error('Error fetching section smart folder data:', error)
    } finally {
      state.isLoading = false
    }
  }

  async function loadConfiguration() {
    if (!myUser?.value) return

    try {
      state.isLoading = true

      const config = pitcherSettings.value?.smart_folder_rule_attributes?.section

      if (isEmpty(config)) {
        state.hierarchyLevels = []
      } else {
        state.hierarchyLevels = config || []
      }
    } catch (error) {
      console.error('Error loading configuration:', error)
    } finally {
      state.isLoading = false
    }
  }

  function navigateToLevel(index: number) {
    state.viewMode = index === state.hierarchyLevels.length ? 'sections' : 'folders'
    state.currentPath = state.currentPath.slice(0, index)
    state.page = 1
    fetchData()
  }

  function setCurrentPath(value: PathSegment[]) {
    state.currentPath = value
  }

  function selectValue(value: string, { skipFetchData = false }: { skipFetchData?: boolean } = {}) {
    if (state.currentPath.length >= state.hierarchyLevels.length) return

    state.currentPath.push({
      value,
      property: state.hierarchyLevels[state.currentPath.length],
    })

    if (state.currentPath.length === state.hierarchyLevels.length) {
      state.viewMode = 'sections'
    }

    if (!skipFetchData) {
      fetchData()
    }
  }

  function toggleViewMode() {
    state.viewMode = state.viewMode === 'folders' ? 'sections' : 'folders'
    fetchData()
  }

  function reset() {
    state.currentPath = []
    state.availableValues = []
    state.matchingSections = []
    state.page = 1
    fetchData()
  }

  function setMatchingSections(sections: SectionById[]) {
    state.matchingSections = sections
  }

  function setIncludeDraft(includeDraft: boolean) {
    state.includeDraft = includeDraft
  }

  return {
    state: readonly(state),
    isSmartFoldersView,
    smartFolderRuleAttributes,
    actions: {
      fetchData,
      loadConfiguration,
      navigateToLevel,
      setCurrentPath,
      selectValue,
      toggleViewMode,
      reset,
      setMatchingSections,
      setIncludeDraft,
    },
  }
}
